import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ENVIRONMENT } from '@cfp/core';
import { TranslateModule } from '@ngx-translate/core';
import { ReportFooter } from '../report-footer';

@Component({
  selector: 'cfp-report-cover-page',
  styleUrls: ['report-cover-page.scss'],
  templateUrl: 'report-cover-page.html',
  imports: [ReportFooter, TranslateModule, DatePipe, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportCoverPage implements OnInit, OnDestroy {
  /** When true (portfolio PDFs), navigate away if not opened via pdfservice. */
  public readonly enforcePdfRoute = input<boolean>(true);
  public readonly showAsScreenPage = input<boolean>(false);
  public readonly portfolioId = input<number | undefined>(undefined);
  public readonly organizationName = input<string>();
  public readonly reportTitle = input<string>();
  public readonly logo = input<string>();
  public readonly coverPageClasses = input<string>();
  public readonly coverImageUrl = model<string | undefined>(undefined);
  /** Relative to assetsDir / ./assets/, used when coverImageUrl is not set. */
  public readonly defaultCoverImage = input<string>('img/report/cover-portfolio.jpg');
  public readonly fallbackCoverImage = input<string>('img/report/cover-portfolio.jpg');
  public readonly showGradient = input<boolean>(false);
  public readonly showFooter = input<boolean>(true);
  public readonly showPoweredBy = input<boolean>(true);
  public readonly showDefaultTitle = input<boolean>(true);
  public readonly showDefaultLogo = input<boolean>(true);
  public readonly customLogo = input<string>();

  protected showGreenBuildingsLogoTitleFooter: boolean = true;
  protected readonly coverPageIsReady = signal<boolean>(false);
  protected readonly currentDate: Date = new Date();

  private coverPageElement = viewChild('coverPageElement', { read: ElementRef });

  protected readonly environment = inject(ENVIRONMENT);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly router = inject(Router);

  constructor() {
    effect(() => {
      // Screen page preview stays in flow above printable content; print-only covers still move to body.
      if (this.showAsScreenPage()) {
        this.coverPageIsReady.set(true);
        return;
      }

      const coverPageElement = this.coverPageElement();
      if (coverPageElement) {
        document.body.insertBefore(coverPageElement.nativeElement, document.body.firstChild);
        this.coverPageIsReady.set(true);
      }
    });
  }

  public ngOnInit(): void {
    const portfolioId = this.portfolioId();
    if (this.enforcePdfRoute()) {
      const isPdfService = this.activatedRoute.snapshot.queryParams?.pdfservice === 'true';
      if (!isPdfService || portfolioId == null || portfolioId < 0) {
        this.router.navigateByUrl(`/energy/${portfolioId != null && portfolioId >= 0 ? portfolioId : ''}`);
        return;
      }
    }

    this.showGreenBuildingsLogoTitleFooter =
      this.environment.components.showReportGreenBuildingsLogoTitleFooter === true;

    if (!this.coverImageUrl() && !this.showGradient()) {
      this.loadCoverImage(this.defaultCoverImage(), this.fallbackCoverImage());
    }
  }

  public ngOnDestroy(): void {
    // Only remove when we relocated the node to document.body.
    if (this.showAsScreenPage()) {
      return;
    }
    const coverPageElement = this.coverPageElement();
    if (coverPageElement) {
      coverPageElement.nativeElement.remove();
    }
  }

  private loadCoverImage(primaryPath: string, fallbackPath: string): void {
    const image = new Image();
    image.onload = () => {
      this.coverImageUrl.set(image.src);
    };
    const assetsPrefix = this.environment.assetsDir ? this.environment.assetsDir : './assets/';
    if (this.environment.assetsDir) {
      image.onerror = () => {
        image.src = `./assets/${fallbackPath}`;
        image.onerror = null;
      };
    }
    image.src = `${assetsPrefix}${primaryPath}`;
  }
}
